import { useCallback, useEffect, useState } from "react";
import { Box, Center, Container, Group, Image, Stack, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Button, LayoutPublic, Modal, PasswordInput, Text } from "components"
import { RouteConstants, ServiceConstants } from "constant";
import { AppUtils, PasswordUtils } from "utils";

import { IRootState } from "config/redux";
import logo from 'assets/logo.svg';
import success from 'assets/success.svg';

import { PasswordRules } from "types";
import { IconCheck, IconX } from "@tabler/icons-react";
import { TFunction } from "i18next";
import { PublicService } from "services";

const { ROUTE_LOGIN } = RouteConstants;
const { REQUEST_PENDING, REQUEST_REGISTRATION_SET_PASSWORD } = ServiceConstants;

const RULES_MODEL: PasswordRules = {
  length: false,
  character: false,
};

const RegistrationSetPassword = () => {
  const { t } = useTranslation();
  const urlParams = new URLSearchParams(window.location.href.split('?')[1]);
  const serviceObject = useSelector((state: IRootState) => state.service.object[REQUEST_REGISTRATION_SET_PASSWORD])
  const [rules, setRules] = useState<PasswordRules>(RULES_MODEL);
  const [error, setError] = useState<string | undefined>(undefined);
  const [successModal, setSuccessModal] = useState<boolean>(false);

  useEffect(() => {
    AppUtils.resolveServiceObject(serviceObject, REQUEST_REGISTRATION_SET_PASSWORD,
      () => setSuccessModal(true),
      (err) => setError(err.data.message)
    );
  }, [serviceObject])

  const form = useForm({
    initialValues: {
      email: urlParams.get('email'),
      identification: urlParams.get('identification'),
      password: '',
      confirmPassword: ''
    },
    validate: {
      email: (value: any) => AppUtils.isEmpty(value) ? t('error.required') : null,
      identification: (value: any) => AppUtils.isEmpty(value) ? t('error.required') : null,
      password: (value: string) => validatePassword(value),
      confirmPassword: (value: string) => validateConfirmPassword(value),
    },
    onValuesChange: (values, previous) => {
      const password = values.password;
      setRules({
        character: PasswordUtils.checkIfHasSpecial(password),
        length: PasswordUtils.checkIfHas8Letters(password)
      });
    },
  });

  const validatePassword = useCallback((val: string) => {
    if (AppUtils.isEmpty(val)) {
      return t('error.required')
    }
    const keys = Object.keys(rules);
    if (keys.length !== keys.filter(el => rules[el as keyof PasswordRules]).length) {
      return '';
    }
    return null;
  }, [rules]);

  const validateConfirmPassword = useCallback((val: string) => {
    if (form.getTransformedValues().password !== val) {
      return t('password.error.match')
    }
    return null;
  }, []);

  const isLoading = useCallback(() => serviceObject && serviceObject.status === REQUEST_PENDING, [serviceObject]);
  const finishRegistration = useCallback(() => {
    setError(undefined);
    const { email, identification, password, confirmPassword } = form.getTransformedValues();
    PublicService.registrationSetPassword(email!, identification!, password, confirmPassword);
  }, [form]);

  return (
    <LayoutPublic>
      <SuccessModal opened={successModal} t={t} />
      <Container
          h={'80vh'}
          display={'flex'}
          style={{ flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}
        >
        <Box mt={"xl"} w={350}>
          <Center>
            <Image src={logo} w={180} fallbackSrc={logo} />
          </Center>
          <Text style={{ fontSize: 24, textAlign: 'center' }}>{'Webkredit.ch'}</Text>
          <Title fw={500} ta={'center'} my={'lg'} mt={40}>{t('registration.password.title')}</Title>
          <Text style={{ textAlign: 'center' }}>{t('registration.password.description')}</Text>
            <Box mt={40}>
              <PasswordInput
                label={t('password.title')}
                key={form.key('password')}
                {...form.getInputProps('password')}
              />
              <Box mt={'sm'}>
                {Object.keys(rules).map((el: any) => (
                  <Group key={`rule-${el}`}>
                    {rules[el as keyof PasswordRules]
                      ? <IconCheck size={20} color={'var(--mantine-primary-color-9)'} />
                      : <IconX size={20} color={'var(--mantine-color-error)'} />}
                    <Text mb={0} c={rules[el as keyof PasswordRules] ? 'var(--mantine-primary-color-9)' : 'var(--mantine-color-error)'}>{t('password.rules.' + el)}</Text>
                  </Group>
                ))}
              </Box>
            </Box>
            <Box mt={'sm'}>
              <PasswordInput
                label={t('password.confirm.title')}
                key={form.key('confirmPassword')}
                {...form.getInputProps('confirmPassword')}
              />
              {error && <Text c={'var(--mantine-color-error)'} mt={'md'}>{error}</Text>}
            </Box>
          <Box mt={40}>
            <Button
              fullWidth={true}
              disabled={!form.isValid() || isLoading()}
              loading={isLoading()}
              onClick={finishRegistration}
            >
              {t('action.submit')}
            </Button>
          </Box>
        </Box>
      </Container>
    </LayoutPublic>
  )
}

interface SuccessModalProps  {
  opened: boolean;
  t: TFunction;
}

const SuccessModal = ({ opened, t }: SuccessModalProps) => {
  const navigate = useNavigate();

  const onClose = useCallback(() => {
    AppUtils.logout();
    navigate(ROUTE_LOGIN);
  }, []);

  return (
    <Modal opened={opened} onClose={onClose} withCloseButton={false}>
      <Stack align={'stretch'} justify={'center'} gap={'lg'}>
        <Center>
          <Image src={success} w={150} h={150} fallbackSrc={logo} />
        </Center>
        <Text size={'lg'} fw={500} ta={'center'} mb={'md'}>{t('registration.password.success.title')}</Text>
        <Button onClick={onClose}>
          {t('registration.password.success.action')}
        </Button>
      </Stack>
    </Modal>
  )
}

export default RegistrationSetPassword;